import React from "react";
import { Link } from "react-router-dom";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { useProducts } from "../context/ProductContext";
import LazyImage from "./LazyImage";

const ProductCard = ({ product, showCategory = false, onWhatsAppClick }) => {
  const [isAnimating, setIsAnimating] = React.useState(false);
  const discount = Math.round(
    ((product.price - product.offerPrice) / product.price) * 100,
  );
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { addToCart, cart, updateQuantity } = useCart();
  const { categories } = useProducts();
  const inWishlist = isInWishlist(product.id);

  // Get the quantity of this product in cart (for default variant)
  const defaultVariant = product.variants[0];
  const cartItem = cart.find(
    (item) =>
      item.id === product.id && item.variant.size === defaultVariant.size,
  );
  const cartQuantity = cartItem ? cartItem.quantity : 0;

  const getCategoryName = (categoryId) => {
    return categories.find((c) => c.id === categoryId)?.name || "";
  };

  const getCategoryIcon = (categoryId) => {
    return categories.find((c) => c.id === categoryId)?.icon || "";
  };

  const handleWishlistClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product.id);
    }
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, defaultVariant, 1);
    triggerAnimation();
  };

  const handleIncreaseQuantity = (e) => {
    e.preventDefault();
    e.stopPropagation();
    updateQuantity(product.id, defaultVariant.size, cartQuantity + 1);
    triggerAnimation();
  };

  const handleDecreaseQuantity = (e) => {
    e.preventDefault();
    e.stopPropagation();
    updateQuantity(product.id, defaultVariant.size, cartQuantity - 1);
    triggerAnimation();
  };

  const triggerAnimation = () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleWhatsApp = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (onWhatsAppClick) {
      onWhatsAppClick(product, defaultVariant, cartQuantity);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden relative flex flex-col animate-slide-in-up">
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative overflow-hidden h-48">
          <LazyImage
            src={product.image}
            alt={product.name}
            className="w-full h-48 object-contain transition-transform duration-500 ease-out hover:scale-110"
          />
          {discount > 0 && (
            <span className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs md:text-sm font-bold">
              {discount}% OFF
            </span>
          )}
          <button
            onClick={handleWishlistClick}
            className={`absolute top-2 left-2 p-1.5 md:p-2 rounded-full transition-all shadow-md ${
              inWishlist
                ? "bg-red-500 text-white"
                : "bg-white text-gray-600 hover:bg-red-50"
            }`}
            title={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
          >
            <svg
              className="w-4 h-4 md:w-5 md:h-5"
              fill={inWishlist ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth={inWishlist ? 0 : 2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>
        </div>
      </Link>

      <div className="p-3 md:p-4 flex flex-col flex-1">
        {showCategory && (
          <div className="mb-2">
            <span className="inline-block bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-semibold">
              {getCategoryIcon(product.category)}{" "}
              {getCategoryName(product.category)}
            </span>
          </div>
        )}
        <Link to={`/product/${product.id}`}>
          <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-1 md:mb-2 line-clamp-2 hover:text-green-600">
            {product.name}
          </h3>
        </Link>
        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
          {product.description}
        </p>
        <div className="text-xs font-semibold text-gray-700 mb-2">
          {product.variants?.length === 1
            ? product.variants[0].size
            : product.variants?.length > 1
              ? `${product.variants[0].size} - ${product.variants[product.variants.length - 1].size}`
              : ""}
        </div>
        <div className="flex items-center mb-3 flex-wrap gap-2">
          {product.variants?.length > 1 ? (
            <span className="text-lg md:text-xl font-bold text-green-600">
              ₹{product.variants[0].offerPrice || product.variants[0].price} - ₹
              {product.variants[product.variants.length - 1].offerPrice ||
                product.variants[product.variants.length - 1].price}
            </span>
          ) : (
            <>
              <span className="text-lg md:text-xl font-bold text-green-600">
                ₹{product.offerPrice}
              </span>
              {product.price !== product.offerPrice && (
                <span className="text-xs md:text-sm text-gray-500 line-through ml-2">
                  ₹{product.price}
                </span>
              )}
            </>
          )}
          {/* <p className="text-[10px] text-red-500 w-full mt-0.5">Additional shipping charges applicable.</p> */}
        </div>

        <div className="mt-auto space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Link
              to={`/product/${product.id}`}
              className="bg-green-500 text-white px-2 py-2 rounded-lg hover:bg-green-600 text-xs md:text-sm font-medium text-center"
            >
              View
            </Link>
            <button
              onClick={handleWhatsApp}
              className="bg-green-500 text-white px-2 py-2 rounded-lg hover:bg-green-600 text-xs md:text-sm font-medium flex items-center justify-center gap-1"
            >
              <svg
                className="w-3 h-3 md:w-4 md:h-4"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              <span className="hidden md:inline">WhatsApp</span>
            </button>
          </div>
          {!product.inStock ? (
            <button
              disabled
              className="w-full border-2 border-gray-300 text-gray-400 py-2 rounded-lg text-xs md:text-sm font-medium cursor-not-allowed"
            >
              Out of Stock
            </button>
          ) : cartQuantity === 0 ? (
            <button
              onClick={handleAddToCart}
              className={`w-full border-2 border-green-500 text-green-600 py-2 rounded-lg hover:bg-green-50 text-xs md:text-sm font-medium flex items-center justify-center gap-1 button-press ripple ${
                isAnimating ? "animate-add-to-cart" : ""
              }`}
            >
              <svg
                className="w-3 h-3 md:w-4 md:h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              Add to Cart
            </button>
          ) : (
            <div
              className={`w-full border-2 border-green-500 text-green-600 rounded-full flex items-center justify-between overflow-hidden h-[38px] md:h-[40px] animate-scale-in ${
                isAnimating ? "animate-add-to-cart" : ""
              }`}
            >
              <button
                onClick={handleDecreaseQuantity}
                className="px-3 md:px-4 h-full hover:bg-green-50 flex items-center justify-center button-press transition-smooth"
                title={
                  cartQuantity === 1 ? "Remove from cart" : "Decrease quantity"
                }
              >
                {cartQuantity === 1 ? (
                  <svg
                    className="w-4 h-4 text-gray-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                ) : (
                  <span className="text-lg md:text-xl font-bold">−</span>
                )}
              </button>
              <span
                className={`flex-1 text-center text-sm md:text-base font-semibold ${
                  isAnimating ? "quantity-change" : ""
                }`}
              >
                {cartQuantity}
              </span>
              <button
                onClick={handleIncreaseQuantity}
                className="px-3 md:px-4 h-full hover:bg-green-50 text-lg md:text-xl font-bold flex items-center justify-center button-press transition-smooth"
              >
                +
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
